'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  FileText,
  ShieldCheck,
  Building2,
  Package,
  Receipt,
  BarChart3,
  ArrowRight,
  CheckCircle2,
  Zap,
  Globe,
  Lock,
  ChevronRight,
} from 'lucide-react';

const features = [
  {
    icon: FileText,
    title: 'GST Invoicing',
    desc: 'Generate CGST/SGST/IGST invoices in seconds. Auto tax calculation, e-invoice ready.',
    span: 'col-span-1 row-span-1',
    accent: '#1a56db',
  },
  {
    icon: ShieldCheck,
    title: 'GSTR Filing',
    desc: 'Auto-populate GSTR-1, GSTR-3B. Reconcile ITC and file returns without manual data entry.',
    span: 'col-span-1 row-span-1',
    accent: '#0e9f6e',
  },
  {
    icon: Building2,
    title: 'Multi-Business',
    desc: 'Manage multiple GSTINs and business entities from a single login.',
    span: 'col-span-1 row-span-1',
    accent: '#7c3aed',
  },
  {
    icon: Package,
    title: 'Inventory Control',
    desc: 'Track stock levels, get low-stock alerts, and manage item-wise HSN codes.',
    span: 'col-span-1 row-span-1',
    accent: '#ea580c',
  },
  {
    icon: Receipt,
    title: 'Expense Tracker',
    desc: 'Categorise business expenses, track budgets, and link to GST input credit.',
    span: 'col-span-1 row-span-1',
    accent: '#0891b2',
  },
  {
    icon: BarChart3,
    title: 'Business Reports',
    desc: 'Revenue trends, party-wise ledgers, HSN summaries — all exportable to PDF.',
    span: 'col-span-1 row-span-1',
    accent: '#be185d',
  },
];

const benefits = [
  'No per-invoice charges — flat pricing',
  'Works for traders, manufacturers & service providers',
  'CBIC-compliant e-invoicing support',
  'Role-based access for accountants & staff',
  'Offline-ready with cloud sync',
  'Dedicated support for GST queries',
];

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#f8fafc] text-[#0f172a]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Navbar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/90 backdrop-blur-md border-b border-[#e2e8f0] shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <AppLogo size={36} />
            <div>
              <span className="font-bold text-lg text-[#0f172a] tracking-tight">Digital Plate</span>
              <p className="text-[10px] text-[#64748b] leading-none">Smart Billing. Better Business.</p>
            </div>
          </div>
          <Link
            href="/login"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1a56db] text-white text-sm font-600 hover:bg-[#1e40af] transition-colors"
          >
            Sign In <ChevronRight size={14} />
          </Link>
        </div>
      </header>
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Background blobs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#1a56db]/6 blur-3xl" />
          <div className="absolute top-60 -left-32 w-96 h-96 rounded-full bg-[#0e9f6e]/5 blur-3xl" />
          <div className="absolute bottom-0 right-1/3 w-80 h-80 rounded-full bg-[#7c3aed]/4 blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1a56db]/8 border border-[#1a56db]/20 mb-6">
              <Zap size={12} className="text-[#1a56db]" />
              <span className="text-xs font-600 text-[#1a56db]">GST Council Compliant · CBIC Approved</span>
            </div>

            <h1 className="text-5xl font-800 text-[#0f172a] leading-[1.1] tracking-tight mb-5">
              GST billing that<br />
              <span className="text-[#1a56db]">actually works</span><br />
              for Indian businesses
            </h1>

            <p className="text-lg text-[#475569] leading-relaxed max-w-xl mb-8">
              Create invoices, file GSTR returns, manage inventory, and track expenses — all in one place. Built for traders, manufacturers, and service providers across India.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Link
                href="/login"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#1a56db] text-white font-600 text-sm hover:bg-[#1e40af] transition-all hover:shadow-lg hover:shadow-[#1a56db]/20 btn-press"
              >
                Get Started Free <ArrowRight size={16} />
              </Link>
              <a
                href="#features"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-[#e2e8f0] bg-white text-[#334155] font-600 text-sm hover:border-[#1a56db]/30 hover:bg-[#f8fafc] transition-colors"
              >
                See Features
              </a>
            </div>

            {/* Trust row */}
            <div className="flex flex-wrap items-center gap-5 mt-10">
              {[
                { icon: Globe, text: 'Pan-India GST support' },
                { icon: Lock, text: 'Bank-grade security' },
                { icon: Zap, text: 'Setup in under 5 minutes' },
              ]?.map((t) => {
                const TIcon = t?.icon;
                return (
                  <div key={t?.text} className="flex items-center gap-1.5 text-xs text-[#64748b]">
                    <TIcon size={13} className="text-[#0e9f6e]" />
                    <span>{t?.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      {/* Stats strip */}
      <section className="border-y border-[#e2e8f0] bg-white py-8 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '10,000+', label: 'Invoices Generated' },
            { value: '500+', label: 'Businesses Onboarded' },
            { value: '₹50Cr+', label: 'Revenue Tracked' },
            { value: '99.9%', label: 'Uptime SLA' },
          ]?.map((s) => (
            <div key={s?.label} className="text-center">
              <p className="text-2xl font-800 text-[#0f172a] tabular-nums">{s?.value}</p>
              <p className="text-xs text-[#64748b] mt-0.5">{s?.label}</p>
            </div>
          ))}
        </div>
      </section>
      {/* Features Bento */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10">
            <p className="text-xs font-700 uppercase tracking-widest text-[#1a56db] mb-2">Everything you need</p>
            <h2 className="text-3xl font-800 text-[#0f172a] leading-tight">
              One platform.<br />Every billing need covered.
            </h2>
          </div>

          {/* Asymmetric bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Large card — GST Invoicing */}
            <div className="md:col-span-2 bg-white rounded-2xl border border-[#e2e8f0] p-7 hover:shadow-md transition-shadow group">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#1a56db18' }}>
                <FileText size={20} style={{ color: '#1a56db' }} />
              </div>
              <h3 className="text-lg font-700 text-[#0f172a] mb-2">GST Invoicing</h3>
              <p className="text-sm text-[#64748b] leading-relaxed mb-4">
                Generate CGST/SGST/IGST invoices in seconds. Auto tax calculation, HSN/SAC codes, e-invoice IRN generation, and PDF export — all built in.
              </p>
              <div className="flex flex-wrap gap-2">
                {['CGST/SGST/IGST', 'E-Invoice IRN', 'PDF Export', 'Bulk Invoicing']?.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full bg-[#f1f5f9] text-xs font-500 text-[#475569]">{tag}</span>
                ))}
              </div>
            </div>

            {/* GSTR Filing */}
            <div className="bg-[#0e9f6e] rounded-2xl p-7 text-white group hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center mb-4">
                <ShieldCheck size={20} className="text-white" />
              </div>
              <h3 className="text-lg font-700 mb-2">GSTR Filing</h3>
              <p className="text-sm text-white/80 leading-relaxed">
                Auto-populate GSTR-1, GSTR-3B. Reconcile ITC and file returns without manual data entry.
              </p>
            </div>

            {/* Multi-Business */}
            <div className="bg-[#0f172a] rounded-2xl p-7 text-white group hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                <Building2 size={20} className="text-white" />
              </div>
              <h3 className="text-lg font-700 mb-2">Multi-Business</h3>
              <p className="text-sm text-white/70 leading-relaxed">
                Manage multiple GSTINs and business entities from a single login. Switch companies in one click.
              </p>
            </div>

            {/* Inventory */}
            <div className="bg-white rounded-2xl border border-[#e2e8f0] p-7 hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#ea580c18' }}>
                <Package size={20} style={{ color: '#ea580c' }} />
              </div>
              <h3 className="text-lg font-700 text-[#0f172a] mb-2">Inventory Control</h3>
              <p className="text-sm text-[#64748b] leading-relaxed">
                Track stock levels, get low-stock alerts, and manage item-wise HSN codes automatically.
              </p>
            </div>

            {/* Reports — large */}
            <div className="md:col-span-1 bg-gradient-to-br from-[#1a56db] to-[#1e40af] rounded-2xl p-7 text-white group hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center mb-4">
                <BarChart3 size={20} className="text-white" />
              </div>
              <h3 className="text-lg font-700 mb-2">Business Reports</h3>
              <p className="text-sm text-white/80 leading-relaxed">
                Revenue trends, party-wise ledgers, HSN summaries — all exportable to PDF.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Benefits + CTA split */}
      <section className="py-20 px-6 bg-white border-y border-[#e2e8f0]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs font-700 uppercase tracking-widest text-[#1a56db] mb-2">Why Digital Plate</p>
            <h2 className="text-3xl font-800 text-[#0f172a] leading-tight mb-6">
              Built for the way<br />Indian businesses work
            </h2>
            <p className="text-sm text-[#64748b] leading-relaxed mb-8">
              Most billing software is built for global markets and retrofitted for GST. Digital Plate is designed ground-up for Indian compliance requirements — from GSTIN validation to GSTR reconciliation.
            </p>
            <ul className="space-y-3">
              {benefits?.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-[#334155]">
                  <CheckCircle2 size={16} className="text-[#0e9f6e] flex-shrink-0 mt-0.5" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA card */}
          <div className="bg-[#f8fafc] rounded-2xl border border-[#e2e8f0] p-8">
            <div className="flex items-center gap-3 mb-6">
              <AppLogo size={44} />
              <div>
                <p className="font-bold text-lg text-[#0f172a]">Digital Plate</p>
                <p className="text-xs text-[#64748b]">Smart Billing. Better Business.</p>
              </div>
            </div>
            <h3 className="text-xl font-700 text-[#0f172a] mb-2">Ready to simplify your billing?</h3>
            <p className="text-sm text-[#64748b] mb-6 leading-relaxed">
              Join hundreds of Indian businesses already using Digital Plate to manage their GST compliance.
            </p>
            <Link
              href="/login"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1a56db] text-white font-600 text-sm hover:bg-[#1e40af] transition-all hover:shadow-lg hover:shadow-[#1a56db]/20 btn-press"
            >
              Sign In to Your Account <ArrowRight size={16} />
            </Link>
            <p className="text-center text-xs text-[#94a3b8] mt-4">
              Need access?{' '}
              <a
                href="mailto:ghotikaramit@gmail.com?subject=Request%20Access%20-%20Digital%20Plate"
                className="text-[#1a56db] hover:underline font-500"
              >
                Request an account
              </a>
            </p>

            <div className="mt-6 pt-6 border-t border-[#e2e8f0] grid grid-cols-3 gap-4 text-center">
              {[
                { icon: ShieldCheck, label: 'GST Compliant' },
                { icon: Lock, label: 'Secure Data' },
                { icon: Zap, label: 'Fast Setup' },
              ]?.map((item) => {
                const IIcon = item?.icon;
                return (
                  <div key={item?.label} className="flex flex-col items-center gap-1.5">
                    <div className="w-8 h-8 rounded-lg bg-[#1a56db]/8 flex items-center justify-center">
                      <IIcon size={14} className="text-[#1a56db]" />
                    </div>
                    <span className="text-xs text-[#64748b]">{item?.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="py-8 px-6 bg-[#f8fafc]">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="text-sm font-600 text-[#334155]">Digital Plate</span>
          </div>
          <p className="text-xs text-[#94a3b8]">© 2026 Digital Plate · GST Billing Suite for India</p>
          <Link href="/login" className="text-xs text-[#1a56db] font-500 hover:underline">
            Sign In →
          </Link>
        </div>
      </footer>
    </div>
  );
}
